import ActionButton from 'components/ActionButton';
import AddRouteToPickPackListModal from 'components/AddRouteToPickPackListModal';
import BackButton from 'components/BackButton';
import InfoModal from 'components/InfoModal';
import { Loading } from 'components/loading/Loading';
import PickPackPDF from 'components/PickPackPDF';
import { IPickPackList } from 'interfaces/pickPackList';
import { Routes } from 'pages/routes';
import React, { useEffect, useState } from 'react';
import { fetchPickPackListDetail, generateFormattedDataForPickPackList, togglePickPackListLock } from 'services';
import { DATE_FORMATS, MODAL_TYPES, formatDate, showError, showSuccess, showWarning } from 'utils';
import styles from './index.module.scss';
import { OrderItemCountPickPack } from './orderItemTable';


const PickPackDetail = props => {
  const { pickPackId } = props.match.params;
  const [pickPack, setPickPack] = useState<Record<string, any>>({});
  const [pickPackRoutes, setPickPackRoutes] = useState<Record<string, any>[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedRouteIds, setSelectedRouteIds] = useState<string[]>([]);
  const [addToPickPackListModalOpen, setAddtoPickPackListModalOpen] = useState<boolean>(false);
  const [forcePDFGenerateModalOpen, setForcePDFGenerateModalOpen] = useState<boolean>(false);
  const [toggleLoading, setToggleLoading] = useState<boolean>(false);
  const [PDFLoading, setPDFLoading] = useState<boolean>(false);
  const [pdfDataAvailable, setPDFDataAvailable] = useState<boolean>(false);
  const [pdfData, setPDFData] = useState<IPickPackList.IPickPackPDFData>();
  const [printDisabled, setPrintDisabled] = useState<boolean>(false);

  const fetchPickPackDetail = async () => {
    try {
      const response = await fetchPickPackListDetail(pickPackId);
      setPickPack(response.pickPackList);
      if (response.pickPackList.formattedData) {
        setPDFDataAvailable(true);
        setPDFData(response.pickPackList.formattedData);
      }
      setPickPackRoutes(response.routes);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPickPackDetail();
  }, []);

  const togglePickPackLock = async () => {
    setToggleLoading(true);
    try {
      await togglePickPackListLock(pickPackId);
      fetchPickPackDetail();
    } catch (e) {
      showError(e);
    } finally {
      setToggleLoading(false);
    }
  };

  const generatePDF = async (force = false) => {
    if (force) {
      setForcePDFGenerateModalOpen(false);
    }
    setPDFLoading(true);
    try {
      const response = await generateFormattedDataForPickPackList(pickPackId);
      showSuccess(response);
      await fetchPickPackDetail();
    } catch (e) {
      showError(e);
    } finally {
      setPDFLoading(false);
    }
  };

  const printQRCodes = () => {
    const printContent = document.getElementById('qr-code');
    const PrintWindow = window.open('', '', 'left=100,top=50,width=900,height=600,toolbar=1,scrollbars=1,status=0');
    if (printContent && PrintWindow) {
      const qrCodeLength = printContent.childNodes[0].childNodes.length;
      for (let i = 0; i < qrCodeLength; i++) {
        const qrCodeElement = printContent.childNodes[0].childNodes[i].childNodes[0];
        const clonedQRCode = qrCodeElement.cloneNode(true);
        const qrCodeContainer = PrintWindow.document.createElement('div');
        qrCodeContainer.style.pageBreakAfter = 'always';
        qrCodeContainer.appendChild(clonedQRCode);
        PrintWindow.document.body.appendChild(qrCodeContainer);
      }
      PrintWindow.document.body.style.display = 'flex';
      PrintWindow.document.body.style.flexDirection = 'column';
      PrintWindow.document.body.style.justifyContent = 'center';
      PrintWindow.document.body.style.alignItems = 'center';
      PrintWindow.focus();
      PrintWindow.print();
    }
  };

  const printPickPackPDF = () => {
    setPrintDisabled(true);
    showWarning('The PDF is being prepared, please wait for a few seconds.');
    setTimeout(() => setPrintDisabled(false), 9999);
    setTimeout(() => window.print(), 10000);
  };

  const setSelectedRoutes = (data: Record<string, any>) => {
    const { selectedRows } = data;
    setSelectedRouteIds(selectedRows.map(row => row.objectId));
  };

  const getPDFInfo = () => {
    if (pickPack.pdfDataGenerationInProgress) {
      return (
        <div className='d-flex'>
          <div className={styles['generation-message']}>
            PDF Data Generation In Progress
          </div>
          <ActionButton
            text={'Force Generate PDF Data'}
            active={!PDFLoading}
            isLoading={PDFLoading}
            onClick={() => setForcePDFGenerateModalOpen(true)}
            customClass={styles['forcePDF']}
          />
        </div>
      );
    } else {
      if (pickPack.formattedData) {
        return (
          <ActionButton
            text={'Print PDF'}
            active={!printDisabled}
            isLoading={printDisabled}
            onClick={printPickPackPDF}
            customClass={'ml-0'}
          />
        );
      } else {
        return (
          <ActionButton
            text={'Generate PDF Data'}
            active={!PDFLoading}
            isLoading={PDFLoading}
            onClick={generatePDF}
            customClass={'ml-0'}
          />
        );
      }
    }
  };

  const getPickPackRoutes = () => (
    <div className={styles['pick-pack-routes']}>
      <div className={styles['subheading']}>
        <div>Routes</div>
        {selectedRouteIds.length > 0 && (
          <ActionButton
            text={'Add to Pick Pack List'}
            active={true}
            isLoading={false}
            onClick={() => setAddtoPickPackListModalOpen(true)}
          />
        )}
      </div>
      <Routes
        {...props}
        data={pickPackRoutes}
        setSelectedRoutes={setSelectedRoutes}
      />
    </div>
  );

  const getOrderItemsTable = () => {
    if (pdfData && pdfDataAvailable) {
      return (
        <div className={styles['pick-pack-routes']}>
          <div className={styles['subheading']}>
            <div>Order Items</div>
          </div>
          <OrderItemCountPickPack data={pdfData} />
        </div>
      );
    } else {
      return null;
    }
  };

  const getPickPackInfo = () => (
    <div className={styles['basic-details']}>
      <div className={'d-flex w-100 justify-content-between'}>
        <div className={styles['details-column']}>
          <div className={styles['info-group']}>
            <div className={styles['label']}>Pick Pack ID</div>
            <div className={`${styles['detail']} ${styles['id']}`}>{pickPack.objectId}</div>
          </div>
          <div className={styles['info-group']}>
            <div className={styles['label']}>Name</div>
            <div className={styles['detail']}>{pickPack.name}</div>
          </div>
          <div className={styles['info-group']}>
            <div className={styles['label']}>Created At</div>
            <div className={styles['detail']}>{formatDate(pickPack.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A)}</div>
          </div>
          <div className={styles['info-group']}>
            <div className={styles['label']}>PDF</div>
            <div className={styles['detail']}>
              {getPDFInfo()}
            </div>
          </div>
        </div>
        <div className={styles['details-column']}>
          <div className={styles['info-group']}>
            <ActionButton
              text={'Toggle Lock'}
              active={!toggleLoading}
              isLoading={toggleLoading}
              onClick={togglePickPackLock}
            />
          </div>
          <div className={styles['info-group']}>
            <div className={styles['label']}>Locked</div>
            <div className={styles['detail']}>{pickPack.locked?.toString()}</div>
          </div>
          <div className={styles['info-group']}>
            <div className={styles['label']}>Pick Pack List No.</div>
            <div className={styles['detail']}>{pickPack.pickPackListNo}</div>
          </div>
        </div>
      </div>
    </div>
  );

  const getHeader = () => (
    <div className={styles['header']}>
      <div className={styles['heading']}>Pick Pack Details</div>
      <div className={styles['header-buttons']}>
        <BackButton />
        {!!pickPackRoutes.length ? (
          <ActionButton
            text={'Print Orders QR Codes'}
            active={true}
            isLoading={false}
            onClick={() => window.open(`/pick-packs/${pickPackId}/qr-codes`)}
          />
        ) : null}
      </div>
    </div>
  );

  if (loading) return <Loading />;

  return (
    <>
      {addToPickPackListModalOpen && (
        <AddRouteToPickPackListModal
          routeIds={selectedRouteIds}
          onSuccess={() => {
            setSelectedRouteIds([]);
            fetchPickPackDetail();
          }}
          toggleModal={() => setAddtoPickPackListModalOpen(!addToPickPackListModalOpen)}
        />
      )}
      {forcePDFGenerateModalOpen && (
        <InfoModal
          cancellable={true}
          type={MODAL_TYPES.CONFIRM_CHANGES}
          title={'Force PDF Generation'}
          message={'You PDF generation is already in process. This is a heavy process which gives load to server. Are you sure you want to restart this and don’t want to wait for the process to finish?'}
          toggleModal={() => setForcePDFGenerateModalOpen(!forcePDFGenerateModalOpen)}
          primaryButtonText={'Force Generate PDF Data'}
          primaryButtonOnClick={() => generatePDF(true)}
          primaryButtonClass={styles['delete-button']}
          secondaryButtonText={'Cancel'}
          secondaryButtonOnClick={() => {
            setForcePDFGenerateModalOpen(false);
          }}
          secondaryButtonClass={''}
        />
      )}
      <div id={'main-container'} className={`${styles['main-container']}`}>
        {getHeader()}
        {getPickPackInfo()}
        {getPickPackRoutes()}
        {getOrderItemsTable()}
      </div>
      <div id={'pdf-preview'} className={styles['pdf-preview']}>
        {(!!pdfData && pdfDataAvailable) ? (
          <PickPackPDF pdfData={pdfData} />
        ) : null}
      </div>
    </>
  );
};

export { PickPackDetail };
